// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useEffect } from 'react';
import {Alert, Modal} from 'ui'
import HelpLink from './collab/HelpLink';
import ModalDivider from './modal/ModalDivider';
import { useTranscript } from 'hooks/transcripts/queries';
import { useIntercoderScore } from 'hooks/useIntercoderScore';
import { useQueryClient } from '@tanstack/react-query';
import { transcriptKeys } from 'hooks/transcripts/queries';


const PARAGRAPH_STYLE = {
  marginBottom: '20px'
}

// react function for Q&A
function QandA (props) {
  return (
    <div style={PARAGRAPH_STYLE}>
      <h4>
        {props.header}
      </h4>

      <div>
        {props.children}
      </div>
    </div>
  )
}

function InterCoderModal({ show, onHide, transcriptID, excerpts }) {
  const queryClient = useQueryClient();

  // Get transcript data for the name. Excerpts arrive as a prop from
  // ComparisonViewContainer's single useTranscriptExcerpts instance (the
  // hook's mount-once contract): this modal stays mounted on the
  // comparison page, and running its own resolution set doubled the
  // page's observer count (measured on the stress transcript).
  const { data: transcript } = useTranscript(transcriptID);

  // Invalidate transcript query when modal opens to fetch fresh data
  useEffect(() => {
    if (show && transcriptID) {
      queryClient.invalidateQueries({ queryKey: transcriptKeys.detail(transcriptID) });
    }
  }, [show, transcriptID, queryClient]);

  // Calculate intercoder score using the hook with fresh excerpts
  const intercoderData = useIntercoderScore(show ? excerpts : null);

  const interCoderNotANumber = () => {
    return isNaN(intercoderData.score) || !intercoderData.score;
  };

  const getScore = () => {
    // if score is NaN or undefined, return 'No Score'
    if (interCoderNotANumber()) {
      return 'No Score';
    }
    return intercoderData.score;
  };

  const getInterCoderErrorMessage = () => {
    if (interCoderNotANumber()) {
      // if the user count is 1 or 0 explain why there is no score
      if (intercoderData.user_count <= 1) {
        return "We are unable to provide an inter-coder reliability score because at least two coders must code this transcript.";
      } else {
        // there are not enough overlapping coded segments to calculate intercoder reliability
        return "We are unable to provide an inter-coder reliability score due to limited overlap in transcript coding. Code more to obtain a meaningful score.";
      }
    }
  };

  const errorMessage = getInterCoderErrorMessage();

  return (
    <div>
      <Modal
        show={show}
        onHide={onHide}
        bsSize='large'
      >
        <Modal.Body>
          <div style={{padding: '20px 45px'}}>
            <div style={PARAGRAPH_STYLE}>
              {!!errorMessage && 
                <Alert variant="info">
                  {errorMessage}
                </Alert>
              }
              <h1>
                {getScore()}
              </h1>
              <h3>
                Intercoder Reliability Score
              </h3>
              <div style={{}}>
                For how your team coded {(transcript?.name || '').trim() ? `"${transcript?.name}"` : 'this transcript'}
              </div>
            </div>

            <div style={{paddingBottom: '15px'}}>
              <ModalDivider/>
            </div>
            <QandA header="What does this score mean?">
              Your intercoder reliability score is a measure of how similarly your team is applying codes to this transcript.
            </QandA>

            <QandA header="How is this score calculated?">
              The score is calculated using <a target="_blank" rel="noopener noreferrer" href="https://en.wikipedia.org/wiki/Krippendorff%27s_alpha">Krippendorff's Alpha</a>.
               This is a standard measure of intercoder reliability.
               The score is calculated on a transcript level and takes into account each person who coded transcript.
               Anyone who did not code this transcript is not included in the score.
            </QandA>

            <QandA header="What is a good score?">
              The score will range between -1 and 1. 
              A score of 0 is considered no better than chance, and 1 is considered full agreement.
              While there is no hard and fast rule, a score of 0.8 or higher is often considered good.
            </QandA>

            <QandA header="Should I use this score?">
              Not necessarily, not all qualitative researchers believe the use of intercoder reliability 
              should be applied universally to all team projects. While some researchers use this score to 
              determine if their team is coding consistently, it is not an irrefutable measurement. Intercoder reliability 
              tends to be more helpful for deductive methodologies with pre-defined codebooks. However, for 
              inductive methodologies, it can have a suppressive effect on the generation of novel theories and insights.
            </QandA>

            <div style={PARAGRAPH_STYLE}>
              <HelpLink
                intercomArticle={process.env['INTERCOM_ARTICLE_INTERCODER_RELIABILITY']}
                glyph="book"
              >
                Learn more about Delve's Intercoder Reliability Score
              </HelpLink>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default InterCoderModal;
